import {
  Button,
  SkeletonBlock,
  SkeletonProvider,
  StepAccordion,
  Tooltip,
  type StepAccordionItem,
  type StepAccordionSubItem,
} from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from '../../ResizableDivider';

export type WizardStepQuestion = StepAccordionSubItem;

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
  disabled?: boolean;
  questions?: WizardStepQuestion[];
  /** Optional action pinned to the right of this step's sidebar header (e.g. an "add" affordance). */
  titleAction?: React.ReactNode;
  /** Optional muted hint shown under the header when this step has no questions (e.g. "No questions yet"). */
  emptyHint?: React.ReactNode;
  /** When true, this step's questions are draggable and the step accepts a dropped question (used to
   *  move an ad-hoc question between group sections). Pairs with WizardProps.onQuestionMove. */
  droppable?: boolean;
}

export interface WizardProps {
  steps: WizardStep[];
  loading?: boolean;
  activeStep: number;
  onStepClick?: (index: number) => void;
  onQuestionClick?: (sectionIndex: number, questionIndex: number) => void;
  /** Called when a question is dragged from one droppable step and dropped onto another. Receives the
   *  source step index, source question index, and destination step index. */
  onQuestionMove?: (fromSectionIndex: number, fromQuestionIndex: number, toSectionIndex: number) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  stepNavigation?: 'completed' | 'none';
  finishLabel?: string;
  nextLoading?: boolean;
  nextDisabled?: boolean;
  previousDisabled?: boolean;
  children: React.ReactNode;
  contentTitle?: React.ReactNode;
  contentDescription?: React.ReactNode;
  contentHeaderActions?: React.ReactNode;
  /** Optional actions rendered inline after the description text (e.g. Enrich button). */
  contentDescriptionActions?: React.ReactNode;
  stepsAriaLabel?: string;
  actions?: React.ReactNode | null;
  /** Controlled sidebar width in px. When provided, the sidebar renders at this width. */
  sidebarWidth?: number;
  /** Called with delta px during drag; consumer should clamp and update sidebarWidth. */
  onSidebarResize?: (delta: number) => void;
  /** Optional header rendered above the step list in the sidebar. */
  sidebarHeader?: React.ReactNode;
  /** Optional footer rendered below the step list in the sidebar (e.g. an "add section" button). */
  sidebarFooter?: React.ReactNode;
  /** Called once on drag end; consumer can persist the final width. */
  onSidebarResizeEnd?: () => void;
  /** When true, hide sidebar and show section dropdown instead (for split pane view). */
  compactMode?: boolean;
  /**
   * Step ids to force open in the sidebar accordion regardless of user toggles (additive). Used to
   * reliably reveal a section while an async action fills it - e.g. the ad-hoc section while captured
   * questions are being written/loaded. Pass `[]` to release.
   */
  forceOpenStepIds?: string[];
}

export function Wizard({
  steps,
  loading = false,
  activeStep,
  onPrevious,
  onNext,
  finishLabel = 'Finish',
  nextLoading = false,
  nextDisabled = false,
  previousDisabled,
  children,
  contentTitle,
  contentDescription,
  contentHeaderActions,
  stepsAriaLabel = 'Wizard steps',
  actions,
  onQuestionClick,
  onQuestionMove,
  onStepClick,
  sidebarWidth,
  onSidebarResize,
  onSidebarResizeEnd,
  sidebarHeader,
  sidebarFooter,
  contentDescriptionActions,
  compactMode = false,
  forceOpenStepIds,
}: WizardProps) {
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;
  const nextLabel = isLastStep ? finishLabel : 'Next';
  const isPreviousDisabled = previousDisabled !== undefined ? previousDisabled : isFirstStep;
  const footerActions =
    actions === undefined ? (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={isPreviousDisabled}
          aria-label="Previous step"
        >
          Previous
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onNext}
          disabled={nextDisabled || nextLoading}
          aria-label={isLastStep ? finishLabel : 'Next step'}
        >
          {nextLoading ? <span className="wizard__btn-spinner" aria-hidden="true" /> : null}
          {nextLabel}
        </Button>
      </>
    ) : (
      actions
    );

  const accordionItems: StepAccordionItem[] = steps.map((step) => ({
    id: step.id,
    title: step.title,
    subItems: step.questions,
    action: step.titleAction,
    ...(step.emptyHint != null ? { emptyHint: step.emptyHint } : {}),
    ...(step.droppable ? { droppable: true } : {}),
  }));

  return (
    <SkeletonProvider loading={loading}>
      <div className={`wizard${compactMode ? ' wizard--compact' : ''}`}>
        {!compactMode && (
          <>
            <div
              className="wizard__sidebar"
              style={sidebarWidth !== undefined ? { width: `${sidebarWidth}px` } : undefined}
            >
              {sidebarHeader ? <div className="wizard__sidebar-header">{sidebarHeader}</div> : null}
              {loading ? (
                <div className="wizard__sidebar-loading" aria-hidden="true">
                  <SkeletonBlock className="wizard__sidebar-loading-item" />
                  <SkeletonBlock className="wizard__sidebar-loading-item" />
                  <SkeletonBlock className="wizard__sidebar-loading-item" />
                  <SkeletonBlock className="wizard__sidebar-loading-item" />
                </div>
              ) : (
                <StepAccordion
                  items={accordionItems}
                  activeIndex={activeStep}
                  ariaLabel={stepsAriaLabel}
                  onItemClick={onStepClick ? (_id, index) => onStepClick(index) : undefined}
                  onSubItemClick={onQuestionClick}
                  onSubItemMove={onQuestionMove}
                  forceOpenIds={forceOpenStepIds}
                />
              )}
              {!loading && sidebarFooter ? (
                <div className="wizard__sidebar-footer">{sidebarFooter}</div>
              ) : null}
            </div>
            {onSidebarResize && (
              <ResizableDivider
                direction="vertical"
                onResize={onSidebarResize}
                onResizeEnd={onSidebarResizeEnd}
                className="wizard__sidebar-divider"
                ariaLabel="Resize wizard steps sidebar"
              />
            )}
          </>
        )}
        <div className="wizard__content">
          {contentTitle || contentDescription || contentHeaderActions || loading ? (
            <div className="wizard__content-header">
              {loading ? (
                <SkeletonBlock className="wizard__content-title-skeleton" />
              ) : contentTitle ? (
                contentDescription ? (
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <h2 className="wizard__content-title">{contentTitle}</h2>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="ui-tooltip wizard__content-description-tooltip"
                        side="bottom"
                        sideOffset={4}
                      >
                        {contentDescription}
                        <Tooltip.Arrow className="ui-tooltip-arrow" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                ) : (
                  <h2 className="wizard__content-title">{contentTitle}</h2>
                )
              ) : null}
              {!loading && contentDescription ? (
                <p className="wizard__content-description">{contentDescription}</p>
              ) : null}
              {!loading && contentDescriptionActions ? (
                <div className="wizard__content-description-actions">
                  {contentDescriptionActions}
                </div>
              ) : null}
              {(contentHeaderActions || loading) && (
                <div className="wizard__content-header-actions">
                  {loading ? (
                    <SkeletonBlock className="wizard__content-header-action-skeleton" />
                  ) : (
                    contentHeaderActions
                  )}
                </div>
              )}
            </div>
          ) : null}
          <div className="wizard__content-body">
            {loading ? (
              <div className="wizard__content-loading" aria-hidden="true">
                <div className="wizard__content-loading-field">
                  <SkeletonBlock className="wizard__content-loading-label" />
                  <SkeletonBlock className="wizard__content-loading-input" />
                </div>
                <div className="wizard__content-loading-field">
                  <SkeletonBlock className="wizard__content-loading-label wizard__content-loading-label--medium" />
                  <SkeletonBlock className="wizard__content-loading-input" />
                </div>
                <div className="wizard__content-loading-field">
                  <SkeletonBlock className="wizard__content-loading-label" />
                  <SkeletonBlock className="wizard__content-loading-input" />
                </div>
              </div>
            ) : (
              children
            )}
          </div>
          {footerActions !== null ? (
            <div className="wizard__footer">
              {loading ? (
                <>
                  <SkeletonBlock className="wizard__footer-action-skeleton wizard__footer-action-skeleton--secondary" />
                  <SkeletonBlock className="wizard__footer-action-skeleton" />
                </>
              ) : (
                footerActions
              )}
            </div>
          ) : null}
        </div>
      </div>
    </SkeletonProvider>
  );
}
